'use client';

import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  ReactNode,
} from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { forwardRef } from 'react';
import cn from '@/ui/utils/cn/cn';
import VectorGraphic from '../VectorGraphic/VectorGraphic';

import $ from './Select.module.scss';

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
type SelectValueProps = Omit<
  ComponentPropsWithoutRef<typeof SelectPrimitive.Value>,
  'children'
> & {
  // Without children Radix portals the selected item text into the trigger,
  // which throws NotFoundError when that node changes before unmount.
  children: ReactNode;
};

const SelectValue = forwardRef<
  ComponentRef<typeof SelectPrimitive.Value>,
  SelectValueProps
>((props, ref) => <SelectPrimitive.Value ref={ref} {...props} />);
SelectValue.displayName = SelectPrimitive.Value.displayName;

const SelectTrigger = forwardRef<
  ComponentRef<typeof SelectPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, disabled, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn($.select, className)}
    {...props}
  >
    {children}
    {!disabled && (
      <SelectPrimitive.Icon asChild>
        <VectorGraphic name="icon-dropdown" />
      </SelectPrimitive.Icon>
    )}
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = forwardRef<
  ComponentRef<typeof SelectPrimitive.ScrollUpButton>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn($.scrollButton, className)}
    {...props}
  >
    <VectorGraphic name="icon-dropdown" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = forwardRef<
  ComponentRef<typeof SelectPrimitive.ScrollDownButton>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn($.scrollButton, className)}
    {...props}
  >
    <VectorGraphic name="icon-dropdown" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = forwardRef<
  ComponentRef<typeof SelectPrimitive.Content>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn($.content, className)}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport className={$.viewport}>
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = forwardRef<
  ComponentRef<typeof SelectPrimitive.Label>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn($.SelectLabel, className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = forwardRef<
  ComponentRef<typeof SelectPrimitive.Item>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn($.selectItem, className)}
    // prevent click event from bubbling up to parent elements
    onClick={e => e.stopPropagation()}
    onMouseDown={e => e.stopPropagation()}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <span className={$.item}>
      <SelectPrimitive.ItemIndicator>
        <VectorGraphic name="icon-check" />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = forwardRef<
  ComponentRef<typeof SelectPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator ref={ref} className={className} {...props} />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
